// A reusable mic button that streams browser-native speech-to-text into any
// text field. The caller decides where to append the transcript.

import { useEffect, useRef } from 'react';
import { useSpeechRecognition } from '../lib/speech.js';
import { usePrefs, type Language } from '../lib/prefs.js';

interface Props {
  onAppend: (text: string) => void;
  /** Fires once when listening stops, with all segments captured in this session. */
  onComplete?: (full: string) => void;
  /** Mirrors listening state to the parent (for UI like avatar mode). */
  onListeningChange?: (listening: boolean) => void;
  /** Override the recognition language (defaults to the user's pref). */
  language?: Language;
  size?: number;
  title?: string;
}

export function MicButton({ onAppend, onComplete, onListeningChange, language, size = 36, title }: Props) {
  const [prefs] = usePrefs();
  const accRef = useRef('');
  const { supported, listening, start, stop } = useSpeechRecognition(language ?? prefs.language, (t) => {
    accRef.current = accRef.current ? accRef.current + ' ' + t : t;
    onAppend(t);
  });

  useEffect(() => { onListeningChange?.(listening); }, [listening, onListeningChange]);
  useEffect(() => {
    // When listening flips false AND we accumulated something, fire onComplete.
    if (!listening && accRef.current.trim() && onComplete) {
      const full = accRef.current.trim();
      accRef.current = '';
      onComplete(full);
    }
  }, [listening, onComplete]);

  function handleClick() {
    if (listening) { stop(); }
    else { accRef.current = ''; start(); }
  }

  if (!supported) return null;

  return (
    <button
      type="button"
      onClick={handleClick}
      title={title ?? (listening ? 'Stop voice input' : 'Voice input')}
      style={{ width: size, height: size }}
      className={`grid place-items-center rounded-full border transition-colors flex-shrink-0 ${
        listening
          ? 'border-amber bg-amber/[0.12] text-amber animate-pulse'
          : 'border-ink/15 text-ink/55 hover:text-ink hover:border-ink/30'
      }`}
    >
      <svg width={size * 0.45} height={size * 0.45} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <rect x="9" y="3" width="6" height="11" rx="3" />
        <path d="M5 11a7 7 0 0 0 14 0" />
        <path d="M12 18v3" />
      </svg>
    </button>
  );
}
